import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';

const SubjectEvaluationConfig = () => {
  const { projectId, subjectId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useApp();

  const [subject, setSubject] = useState(null);
  const [selectedOA, setSelectedOA] = useState('');
  const [selectedSkill, setSelectedSkill] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [oaDescription, setOaDescription] = useState('');
  const [indicators, setIndicators] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mock data para OAs y habilidades
  const availableOAs = [
    { id: 'oa1', code: 'OA1', name: 'Comprender y aplicar conceptos matemáticos básicos' },
    { id: 'oa2', code: 'OA2', name: 'Resolver problemas de operaciones aritméticas' },
    { id: 'oa3', code: 'OA3', name: 'Analizar patrones y relaciones numéricas' },
    { id: 'oa4', code: 'OA4', name: 'Representar información mediante gráficos' },
  ];

  const availableSkills = [
    { id: 'recordar', name: 'Recordar', level: 1 },
    { id: 'comprender', name: 'Comprender', level: 2 },
    { id: 'aplicar', name: 'Aplicar', level: 3 },
    { id: 'analizar', name: 'Analizar', level: 4 },
    { id: 'evaluar', name: 'Evaluar', level: 5 },
    { id: 'crear', name: 'Crear', level: 6 },
  ];

  useEffect(() => {
    loadSubjectData();
  }, [projectId, subjectId]);

  const loadSubjectData = async () => {
    try {
      setLoading(true);
      // Mock data - aquí se haría la llamada real a la API
      setSubject({
        id: subjectId,
        name: 'Matemáticas',
        teacherName: 'Profesor Demo'
      });

      // Cargar indicadores existentes
      setIndicators([
        // Ejemplo de indicador guardado
        // { id: 1, oa: 'OA1', skill: 'aplicar', description: 'Descripción...', status: 'pendiente' }
      ]);
    } catch (error) {
      showError('Error al cargar la asignatura');
    } finally {
      setLoading(false);
    }
  };

  const handleOAChange = (oaId) => {
    setSelectedOA(oaId);
    const oa = availableOAs.find(o => o.id === oaId);
    if (oa) {
      setOaDescription(oa.name);
    }
  };

  const handleAddIndicator = () => {
    if (!selectedOA || !selectedSkill || !selectedType) {
      showError('Debes seleccionar un OA, una habilidad y el tipo de indicador');
      return;
    }

    if (!oaDescription.trim()) {
      showError('Debes completar la descripción del indicador');
      return;
    }

    const oa = availableOAs.find(o => o.id === selectedOA);
    const skill = availableSkills.find(s => s.id === selectedSkill);

    const newIndicator = {
      id: Date.now(),
      oaId: selectedOA,
      oaCode: oa.code,
      oaName: oa.name,
      skillId: selectedSkill,
      skillName: skill.name,
      skillLevel: skill.level,
      type: selectedType,
      description: oaDescription,
      status: 'pendiente',
      createdAt: new Date().toISOString()
    };

    setIndicators([...indicators, newIndicator]);

    // Resetear formulario
    setSelectedOA('');
    setSelectedSkill('');
    setSelectedType('');
    setOaDescription('');

    showSuccess('Indicador agregado correctamente');
  };

  const handleDeleteIndicator = (indicatorId) => {
    setIndicators(indicators.filter(i => i.id !== indicatorId));
    showSuccess('Indicador eliminado');
  };

  const handleSaveConfiguration = () => {
    if (indicators.length === 0) {
      showError('Debes agregar al menos un indicador');
      return;
    }

    // Aquí se guardaría en la API
    showSuccess('Configuración guardada correctamente');
    navigate(`/projects/${projectId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/projects/${projectId}`)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Volver al proyecto
          </button>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Configuración de Evaluación
            </h1>
            <p className="text-gray-600">
              Asignatura: <span className="font-semibold">{subject?.name}</span> • Profesor: {subject?.teacherName}
            </p>
          </div>
        </div>

        {/* Formulario para agregar indicador */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Agregar Indicador de Evaluación
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {/* Selector de OA */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Objetivo de Aprendizaje (OA) *
              </label>
              <select
                value={selectedOA}
                onChange={(e) => handleOAChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Seleccionar OA...</option>
                {availableOAs.map((oa) => (
                  <option key={oa.id} value={oa.id}>
                    {oa.code} - {oa.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Selector de Habilidad */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Habilidad *
              </label>
              <select
                value={selectedSkill}
                onChange={(e) => setSelectedSkill(e.target.value)}
                disabled={!selectedOA}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Seleccionar habilidad...</option>
                {availableSkills.map((skill) => (
                  <option key={skill.id} value={skill.id}>
                    Nivel {skill.level}: {skill.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Selector de Tipo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Indicador *
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                disabled={!selectedOA}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Seleccionar tipo...</option>
                <option value="proceso">Proceso</option>
                <option value="producto">Producto</option>
              </select>
            </div>
          </div>

          {/* Descripción del indicador */}
          {selectedOA && selectedSkill && selectedType && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción del Indicador *
              </label>
              <textarea
                value={oaDescription}
                onChange={(e) => setOaDescription(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Edita o completa la descripción del indicador..."
              />
              <p className="text-xs text-gray-500 mt-2">
                Puedes modificar la descripción base del OA para ajustarla al indicador específico
              </p>
            </div>
          )}

          {/* Botón agregar */}
          <div className="flex justify-end">
            <button
              onClick={handleAddIndicator}
              disabled={!selectedOA || !selectedSkill || !selectedType || !oaDescription.trim()}
              className="bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Agregar Indicador
            </button>
          </div>
        </div>

        {/* Lista de indicadores agregados */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Indicadores Agregados ({indicators.length})
            </h2>
          </div>

          {indicators.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              <p className="text-gray-500">No hay indicadores agregados</p>
              <p className="text-sm text-gray-400 mt-1">Agrega el primer indicador usando el formulario de arriba</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Indicadores de Proceso */}
              {indicators.filter(i => i.type === 'proceso').length > 0 && (
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg font-semibold text-sm">
                      Proceso
                    </div>
                    <span className="text-sm text-gray-500">
                      ({indicators.filter(i => i.type === 'proceso').length} {indicators.filter(i => i.type === 'proceso').length === 1 ? 'indicador' : 'indicadores'})
                    </span>
                  </div>
                  <div className="space-y-4">
                    {indicators.filter(i => i.type === 'proceso').map((indicator, index) => (
                      <div key={indicator.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-start space-x-3 flex-1">
                            <div className="bg-primary-100 text-primary-700 rounded-lg px-3 py-1 text-sm font-semibold flex-shrink-0">
                              #{index + 1}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                  {indicator.oaCode}
                                </span>
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                  {indicator.skillName} (Nivel {indicator.skillLevel})
                                </span>
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                  {indicator.status}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700">{indicator.description}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteIndicator(indicator.id)}
                            className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
                            title="Eliminar indicador"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Indicadores de Producto */}
              {indicators.filter(i => i.type === 'producto').length > 0 && (
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="bg-green-100 text-green-700 px-3 py-1 rounded-lg font-semibold text-sm">
                      Producto
                    </div>
                    <span className="text-sm text-gray-500">
                      ({indicators.filter(i => i.type === 'producto').length} {indicators.filter(i => i.type === 'producto').length === 1 ? 'indicador' : 'indicadores'})
                    </span>
                  </div>
                  <div className="space-y-4">
                    {indicators.filter(i => i.type === 'producto').map((indicator, index) => (
                      <div key={indicator.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-start space-x-3 flex-1">
                            <div className="bg-primary-100 text-primary-700 rounded-lg px-3 py-1 text-sm font-semibold flex-shrink-0">
                              #{index + 1}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                  {indicator.oaCode}
                                </span>
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                  {indicator.skillName} (Nivel {indicator.skillLevel})
                                </span>
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                  {indicator.status}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700">{indicator.description}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteIndicator(indicator.id)}
                            className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
                            title="Eliminar indicador"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Botones de acción */}
        <div className="mt-6 flex justify-end space-x-4">
          <button
            onClick={() => navigate(`/projects/${projectId}`)}
            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSaveConfiguration}
            disabled={indicators.length === 0}
            className="bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Guardar Configuración
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubjectEvaluationConfig;
